import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { TUTORIAL_STEPS } from './tutorialSteps';
import { setTutorialActive } from './tutorialInterceptor';
import {
  TUTORIAL_CONNECTION_ID,
  TUTORIAL_ROUTE_ID,
  TUTORIAL_ROUTE_RECORD_ID,
} from './tutorialData';

// GUNDI-5665 — in-app guided tour of the new UI. TEMPORARY (≤3 weeks): the
// whole tutorial lives in src/components/tutorial/; see the removal manifest
// in tutorialInterceptor.ts. State is React-only ON PURPOSE — a page refresh
// must end the tour (product call), so nothing is persisted anywhere.
type TutorialStatus = 'idle' | 'confirming' | 'running';

type TutorialState = {
  status: TutorialStatus;
  stepIndex: number;
  requestStart: () => void; // banner link → opens the confirm modal
  cancelStart: () => void; // modal "Cancel"
  confirmStart: () => void; // modal "Start tour"
  advance: () => void; // the ">" button; last step ends the tour
};

const TutorialContext = createContext<TutorialState | null>(null);

const TUTORIAL_URL_IDS = [
  TUTORIAL_ROUTE_ID,
  TUTORIAL_CONNECTION_ID,
  TUTORIAL_ROUTE_RECORD_ID,
];

export function TutorialProvider({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [status, setStatus] = useState<TutorialStatus>('idle');
  const [stepIndex, setStepIndex] = useState(0);

  // The interceptor flag is module-level so a refresh disarms it for free —
  // but an UNMOUNT of this provider (e.g. the tree being swapped) is not a
  // refresh: without this cleanup a mid-tour unmount would leave the flag
  // armed, and ordinary requests afterwards would get tutorial fixtures.
  useEffect(() => () => setTutorialActive(false), []);

  // The tour's mock pages only resolve while the tour is running. Reaching one
  // any other way — a refresh mid-tour (which by design ends it), the back
  // button after finishing, a bookmarked URL — would render a 404, so send
  // those visits home instead.
  //
  // While the tour IS running, the same effect holds the other door shut: the
  // overlay swallows clicks but not the browser's Back button, and nothing
  // else re-navigates until a step whose path differs (steps 7–12 all share
  // the mock Route's path) — Back would park the user on a real page with the
  // overlay up and every spotlight timed out. In-tour navigation the tour
  // itself causes is recognized by its tutorial ids (every in-tour path still
  // contains one), so only a genuinely foreign location snaps back. The guided
  // attach used to land deeper than the step's declared path; the flow map's
  // selection is component state now, so it stays on that exact path.
  useEffect(() => {
    const onTutorialUrl = TUTORIAL_URL_IDS.some((id) =>
      location.pathname.includes(id),
    );
    if (status !== 'running') {
      if (onTutorialUrl) navigate('/', { replace: true });
      return;
    }
    const expected = TUTORIAL_STEPS[stepIndex].path;
    if (location.pathname !== expected && !onTutorialUrl) {
      navigate(expected, { replace: true });
    }
  }, [status, stepIndex, location.pathname, navigate]);

  // A session expiry mid-tour shows the login modal UNDER the overlay
  // (z-100 vs z-10000), where the scrim dims it, the overlay swallows its
  // clicks and the focus trap keeps Tab pinned to the Next button — a dead
  // end whose only exit was a refresh. Real requests do run mid-tour (the
  // first steps sit on the real Routes list), so this is reachable. Ending
  // the tour unmounts the overlay and hands the page to the modal.
  useEffect(() => {
    if (status !== 'running') return;
    const endTour = () => {
      setStatus('idle');
      setStepIndex(0);
      setTutorialActive(false);
    };
    window.addEventListener('api401', endTour);
    return () => window.removeEventListener('api401', endTour);
  }, [status]);

  const requestStart = useCallback(() => setStatus('confirming'), []);
  const cancelStart = useCallback(() => setStatus('idle'), []);

  const confirmStart = useCallback(() => {
    setStepIndex(0);
    setStatus('running');
    // Arm the mock-data interceptor BEFORE navigating, so the first request
    // the tour's page fires is already answered locally. The flag is
    // module-level, so a refresh disarms it at the same moment it ends the
    // tour — there is nothing to clean up.
    setTutorialActive(true);
    // Steps own their page: land on the first step's page explicitly so the
    // tour starts identically from any screen.
    navigate(TUTORIAL_STEPS[0].path);
  }, [navigate]);

  // Navigation and the interceptor flag are side effects, so they run here
  // rather than inside a setState updater (which React may invoke twice).
  const advance = useCallback(() => {
    // Perform the click this step just described (open the drawer, tick the
    // candidate, attach, open the node) BEFORE showing the next message, so
    // the next step's target exists by the time the overlay polls for it.
    //
    // Guarded by page (review finding): the actions click document-wide
    // test-ids that also exist on REAL Route pages, and are only safe
    // because the tour's navigation landed on the mock Route. If that
    // assumption ever breaks, skipping the click avoids a real PATCH —
    // while still advancing, so the tour can be clicked through to its
    // end instead of stalling with no exit.
    const onTutorialPage =
      location.pathname.includes(TUTORIAL_ROUTE_ID) ||
      location.pathname.includes(TUTORIAL_CONNECTION_ID);
    if (onTutorialPage) TUTORIAL_STEPS[stepIndex].advanceAction?.();
    const next = stepIndex + 1;
    if (next >= TUTORIAL_STEPS.length) {
      // Tour over: back to the Routes list (product call).
      setStatus('idle');
      setStepIndex(0);
      setTutorialActive(false);
      navigate('/');
      return;
    }
    setStepIndex(next);
    if (TUTORIAL_STEPS[next].path !== TUTORIAL_STEPS[stepIndex].path)
      navigate(TUTORIAL_STEPS[next].path);
  }, [navigate, stepIndex, location.pathname]);

  return (
    <TutorialContext.Provider
      value={{
        status,
        stepIndex,
        requestStart,
        cancelStart,
        confirmStart,
        advance,
      }}
    >
      {children}
    </TutorialContext.Provider>
  );
}

export function useTutorial(): TutorialState {
  const ctx = useContext(TutorialContext);
  if (!ctx) throw new Error('useTutorial must be used inside TutorialProvider');
  return ctx;
}
